import { ChangeDetectionStrategy, Component, computed, inject, signal, viewChild } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { PopoverModule, Popover } from 'primeng/popover';
import { Ripple } from 'primeng/ripple';

import { BranchSessionService, BranchSessionOption } from '@auth/services/branch-session.service';
import { ToastService } from '@core/services/toast.service';

/**
 * Selector de sede activa de la barra superior.
 *
 * **No es un filtro**: cambiar de sede reemite el access token, así que afecta a TODO lo que la
 * sesión ve y escribe a partir de ese momento. Por eso se comporta como un cambio de contexto
 * —confirmación visual, bloqueo mientras viaja, recarga de la vista— y no como un desplegable más.
 *
 * Se oculta cuando el usuario tiene una sola sede: un selector de un elemento solo añade ruido.
 *
 * Con **cero** sedes alcanzables no se oculta: avisa. Ese usuario no ve un solo almacén —el
 * backend le responde 409— así que no puede facturar ni mover inventario, y cada pantalla se
 * limita a decirle «no hay registros». Este es el único sitio de la aplicación donde ese estado
 * se puede contar una vez y valer para todas.
 */
@Component({
  selector: 'app-branch-switcher',
  standalone: true,
  imports: [ButtonModule, PopoverModule, Ripple],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (session.hasMultipleBranches()) {
      <button
        pButton
        pRipple
        type="button"
        text
        rounded
        class="flex-shrink-0 gap-2"
        [disabled]="session.isSwitching()"
        [title]="'Sede activa: ' + activeLabel() + '. Pulse para cambiar.'"
        [attr.aria-label]="'Sede activa: ' + activeLabel() + '. Pulse para cambiar de sede.'"
        (click)="panel().toggle($event)"
      >
        <i class="pi pi-building"></i>
        <span class="hidden md:inline text-sm font-medium">{{ activeLabel() }}</span>
      </button>

      <p-popover #panelRef [style]="{ width: '17rem' }">
        <div class="flex flex-col">
          <span class="text-xs uppercase tracking-wide text-surface-500 px-2 pb-2"> Cambiar de sede </span>

          @for (branch of session.branches(); track branch.id) {
            <button
              type="button"
              class="flex items-center gap-3 w-full px-2 py-2 rounded-md text-left hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              [disabled]="session.isSwitching() || branch.id === session.activeBranchId()"
              (click)="onSelect(branch)"
            >
              <i
                class="pi"
                [class.pi-check-circle]="branch.id === session.activeBranchId()"
                [class.pi-circle]="branch.id !== session.activeBranchId()"
                [class.text-primary]="branch.id === session.activeBranchId()"
              ></i>
              <span class="flex-1 min-w-0">
                <span class="block truncate text-sm">{{ branch.name }}</span>
                @if (branch.code) {
                  <span class="block text-xs text-surface-500">{{ branch.code }}</span>
                }
              </span>
            </button>
          }

          <!-- La operación se hace SIEMPRE en la sede activa; decirlo aquí evita que el usuario
               interprete el selector como un filtro de consulta y se pregunte por qué "no ve" lo
               de la otra sede. -->
          <p class="text-xs text-surface-500 px-2 pt-3 m-0 leading-snug">
            Registra y consulta en la sede activa. Cambiarla afecta a toda la sesión.
          </p>
        </div>
      </p-popover>
    } @else if (session.hasNoReachableBranch()) {
      <span
        class="flex items-center gap-2 flex-shrink-0 px-2 text-sm font-medium text-orange-500"
        title="No tienes ninguna sede activa asignada, así que no verás almacenes ni podrás facturar. Pide que te asignen una en Configuración › Sucursales."
      >
        <i class="pi pi-exclamation-triangle"></i>
        <span class="hidden md:inline">Sin sede asignada</span>
      </span>
    }
  `,
})
export class BranchSwitcherComponent {
  protected readonly session = inject(BranchSessionService);
  private readonly toast = inject(ToastService);

  protected readonly panel = viewChild.required<Popover>('panelRef');

  private readonly _pending = signal<string | null>(null);

  protected readonly activeLabel = computed(() => this.session.activeBranch()?.name ?? 'Sin sede');

  protected onSelect(branch: BranchSessionOption): void {
    if (branch.id === this.session.activeBranchId() || this.session.isSwitching()) return;

    this._pending.set(branch.id);
    this.panel().hide();

    this.session.changeActiveBranch(branch.id).subscribe({
      next: () => {
        this._pending.set(null);
        this.toast.success(`Ahora está operando en ${branch.name}.`);
        // Recarga dura a propósito: el token cambió y con él el alcance de TODO lo que ya está en
        // pantalla y en las cachés de cada feature. Refrescar solo la vista actual dejaría datos
        // de la sede anterior en memoria, que es exactamente la confusión que hay que evitar.
        window.location.reload();
      },
      error: () => {
        this._pending.set(null);
        this.toast.error('No se pudo cambiar de sede. Intente nuevamente.');
      },
    });
  }
}
